import React, { useCallback, useMemo } from "react";
import { Spinner } from "@heroui/react";
import { Typography, UniversalTable } from "../../../../components";
import { useTranslations } from "../../../../contexts/translation.context";
import { Creator } from "../../../../api/staff";
import { EmptyContentIc } from "../../../../components/icons";
import {
  CreatorActionsCell,
  CreatorBalanceCell,
  CreatorEmailCell,
  CreatorNameCell,
  CreatorInfoCell,
  CreatorIdCell,
  CreatorStatusCell,
  CreatorCreatedAtCell
} from "./index";
import { getCreatorsTableColumns } from "../constants";

interface CreatorsTableProps {
  creators: Creator[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  sortBy?: string;
  onSort?: (sortBy: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRefresh?: () => void;
}

/**
 * CreatorsTable Component
 *
 * Main table component for displaying creators.
 * Uses UniversalTable from the component library and custom cell renderers.
 */
const CreatorsTable: React.FC<CreatorsTableProps> = ({
  creators,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  sortBy,
  onSort,
  onPageChange,
  onPageSizeChange,
  onRefresh,
}) => {
  const { t } = useTranslations();

  const columns = useMemo(() => getCreatorsTableColumns(t), [t]);

  const renderCell = useCallback((creator: Creator, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return <CreatorIdCell creator={creator} />;
      case "name":
        return <CreatorInfoCell creator={creator} />;
      case "email":
        return <CreatorEmailCell creator={creator} />;
      case "status":
        return <CreatorStatusCell creator={creator} />;
      case "balance":
        return <CreatorBalanceCell creator={creator} />;
      case "created_at":
        return <CreatorCreatedAtCell creator={creator} />;
      case "actions":
        return <CreatorActionsCell creator={creator} onRefresh={onRefresh} />;
      default:
        return null;
    }
  }, [onRefresh]);

  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <EmptyContentIc />
        <div className="flex flex-col gap-[6px]">
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {t('admin.creators.empty.title')}
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {t('admin.creators.empty.description')}
          </Typography>
        </div>
      </div>
    </div>
  );

  if (isLoading && creators.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <UniversalTable
      data={creators}
      columns={columns}
      renderCell={renderCell}
      emptyContent={emptyContent}
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      sortBy={sortBy}
      onSort={onSort}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default CreatorsTable;
